/**
 * Pure formatting + grouping helpers shared across the Cafeteria Mode
 * surfaces (Today / serve / history) and the production-record wizards the
 * Production Records page hosts. Kept side-effect free + unit-tested.
 */

import type {
  CafeteriaDeliveryLineWire,
  ServeDeliverableRowWire,
  ServiceComponentTotalWire,
  ServiceOrderItemWire,
  StudentAllergenWire,
} from '@/api/cafeteria'
import type {
  ProductionFillDeliverable,
  ProductionFillGradeRow,
  ProductionFillLineTarget,
  ProductionFillResponse,
} from '@/api/cafeteria-production'
import { TEMP_CLASS_PREFILL_F } from '@/lib/cafeteria/aggregate-deliveries'
import { formatShortDay } from '@/lib/chefs/format'

// Display labels + chronological (time-of-day) order come from the app-wide
// canonical source so every surface renders mealtimes identically and in order.
export { MEALTIME_LABELS, MEALTIMES as MEALTIME_ORDER } from '@/lib/mealtime'

export function todayLocalIso(now: Date = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseIsoDateParam(raw: string | null): string | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null
  }
  const parsed = new Date(`${raw}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : raw
}

/** Shift an ISO yyyy-mm-dd calendar day by `days` (local-safe: pure date math). */
export function shiftIsoDate(iso: string, days: number): string {
  const parsed = new Date(`${iso}T00:00:00`)
  parsed.setDate(parsed.getDate() + days)
  return todayLocalIso(parsed)
}

/** Clamp an ISO yyyy-mm-dd day into [min, max] (lexicographic = chronological). */
export function clampIsoDate(iso: string, min: string, max: string): string {
  if (iso < min) {
    return min
  }
  return iso > max ? max : iso
}

/**
 * An ISO yyyy-mm-dd service date as a LOCAL calendar Date (midnight local),
 * or null when it doesn't parse. Local — not UTC — because a service date is
 * a wall-calendar day at the campus, and the T00:00:00 suffix is what keeps
 * `new Date()` from reading a bare yyyy-mm-dd as UTC and shifting the weekday.
 */
export function serviceDateToDate(value: string): Date | null {
  const parsed = new Date(`${value}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatServiceDateLabel(value: string): string {
  const parsed = serviceDateToDate(value)
  if (!parsed) {
    return value
  }
  return parsed.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** Compact service-date label for pills / manifest rows, e.g. "Mon 07/27". */
export function formatShortServiceDateLabel(value: string): string {
  const parsed = serviceDateToDate(value)
  return parsed ? formatShortDay(parsed) : value
}

/** The serve header's second line, "Wed, Sep 10" (weekday first: a line worker reads the day, not the date). */
export function formatServeHeaderDateLabel(value: string): string {
  const parsed = serviceDateToDate(value)
  if (!parsed) {
    return value
  }
  return parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export function formatRelativeServiceDateLabel(value: string, now: Date = new Date()): string {
  if (value === todayLocalIso(now)) {
    return 'Today'
  }
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  return value === todayLocalIso(yesterday) ? 'Yesterday' : formatServiceDateLabel(value)
}

/**
 * Whether the serve page must render read-only (PROD-1076 preview). A future
 * service date is ALWAYS preview regardless of the param — the URL is
 * user-editable and serving a day that hasn't happened corrupts records.
 * `?preview=1` additionally forces preview for a today view (the dashboard's
 * Preview action on a blocked Serve card). Past days stay live: History's
 * retro-completion flow serves through this page.
 */
export function isServePreviewOnly(serviceDate: string, previewParam: string | null, now: Date = new Date()): boolean {
  return serviceDate > todayLocalIso(now) || previewParam === '1'
}

export function isServed(item: Pick<ServiceOrderItemWire, 'status' | 'servedAt'>): boolean {
  return item.status === 'SERVED' || item.servedAt != null
}

/**
 * Default temperature for a delivery line's "Confirm temperatures" input.
 * Prefers an existing DELIVERY (receipt) reading; otherwise falls back to the
 * chef's FACILITY (kitchen-departure) temp so the worker confirms a sensible
 * pre-filled value instead of re-keying it. Returns null when neither exists.
 */
export function deliveryTempDefault(
  line: Pick<CafeteriaDeliveryLineWire, 'deliveryTemperatureF' | 'facilityTemperatureF'>,
): number | null {
  return line.deliveryTemperatureF ?? line.facilityTemperatureF ?? null
}

/**
 * Per-component planned/served totals for the Serve summary rail. Mirrors
 * the server's `computeComponentTotals`, but takes an `isItemServed`
 * predicate so the client can fold in local (uncommitted) serve state —
 * the rail must react to the +/− steppers and student toggles before the
 * mealtime is committed. A component counts as served only when its item
 * is served AND the component was accepted (offer-vs-serve).
 */
export function recomputeComponentTotals(
  items: ReadonlyArray<ServiceOrderItemWire>,
  isItemServed: (item: ServiceOrderItemWire) => boolean,
): ServiceComponentTotalWire[] {
  const byId = new Map<string, ServiceComponentTotalWire>()
  for (const item of items) {
    const served = isItemServed(item)
    for (const component of item.components) {
      let entry = byId.get(component.componentId)
      if (!entry) {
        entry = {
          componentId: component.componentId,
          componentName: component.componentName,
          componentKind: component.componentKind ?? null,
          planned: 0,
          served: 0,
        }
        byId.set(component.componentId, entry)
      }
      // Count SERVINGS, not rows — an order line with quantity 2 plans (and
      // serves) 2 of each component, keeping the rail in step with the
      // servings-grained session totals.
      entry.planned += item.quantity
      if (served && component.accepted) {
        entry.served += item.quantity
      }
    }
  }
  // First-seen order — mirrors the server's `computeComponentTotals`, which
  // preserves authored (bundle sortOrder) component order.
  return Array.from(byId.values())
}

/**
 * Units that are pure counts — the leading "1" already answers "one what?",
 * so we don't surface the unit. Everything else is a measure (cup, oz, lb, …)
 * worth leading the line with.
 */
const COUNT_UNITS = new Set(['each', 'x', 'ct', 'count', 'unit', 'units', 'piece', 'pieces', 'whole'])

/**
 * Serve-card meal line label. Leads with the measured portion ("0.75 cup
 * Mandarin Oranges Cup"); keeps "N Name" for count items and when no portion
 * is known. `portion` is the server-formatted "{value} {unit}" string.
 * `quantity` is the ordered serving count — a 2× order line renders
 * "2 Cheese Pizza Square" / "2 × 0.75 cup Mandarin Oranges Cup" so the
 * worker hands out every paid-for serving.
 */
export function formatMealLineLabel(label: string, portion: string | null, quantity = 1): string {
  if (!portion) {
    return `${quantity} ${label}`
  }
  const unit = portion.trim().split(/\s+/).pop()?.toLowerCase() ?? ''
  if (COUNT_UNITS.has(unit)) {
    return `${quantity} ${label}`
  }
  return quantity > 1 ? `${quantity} × ${portion} ${label}` : `${portion} ${label}`
}

/**
 * `formatMealLineLabel` split into the bold quantity/portion prefix and the
 * component name, for card rows that emphasize "how much" over "of what".
 * Invariant: `${prefix} ${name}` === `formatMealLineLabel(label, portion, quantity)`
 * so screen cards and the print roster always read identically.
 */
export function splitMealLineLabel(
  label: string,
  portion: string | null,
  quantity = 1,
): { prefix: string; name: string } {
  if (!portion) {
    return { prefix: `${quantity}`, name: label }
  }
  const unit = portion.trim().split(/\s+/).pop()?.toLowerCase() ?? ''
  if (COUNT_UNITS.has(unit)) {
    return { prefix: `${quantity}`, name: label }
  }
  return quantity > 1 ? { prefix: `${quantity} × ${portion}`, name: label } : { prefix: portion, name: label }
}

/**
 * Milk is a BEVERAGE whose name says so — the same word-boundary heuristic as
 * `isBeverageLike` in @ordo/constants/bundle-title (template-bucket units and
 * legacy milk lines carry no component kind, so the name is all there is).
 */
const MILK_NAME = /\bmilk\b/i

/**
 * Serve-surface rank for one part of a meal (Tasker 6d441bd7, St. Hugo): the
 * main entree leads — the protein is what the worker plates first, so it must
 * never be the truncated tail — and milk/beverages trail. Everything else
 * (null kinds included) is middle and keeps its authored order. Deliberately
 * NOT `bundleTitleKindRank` from @ordo/constants: that fully re-orders and
 * alphabetizes the middle, while a plating label preserves authored order.
 */
function serveMealPartRank(kind: string | null | undefined, name: string): number {
  if (kind === 'MAIN_ENTREE') {
    return 0
  }
  return kind === 'BEVERAGE' || (!kind && MILK_NAME.test(name)) ? 2 : 1
}

interface ServeMealPart {
  name: string
  kind: string | null
}

/** Stable main-first / beverage-last ordering (see `serveMealPartRank`). */
function orderMealPartsMainFirst(parts: ReadonlyArray<ServeMealPart>): ServeMealPart[] {
  // Array.prototype.sort is stable — same-rank parts keep authored order.
  return [...parts].sort((a, b) => serveMealPartRank(a.kind, a.name) - serveMealPartRank(b.kind, b.name))
}

/**
 * The meal name Cafeteria serve surfaces display while plating: composed from
 * the bundle's component names ordered main-first ("Halal Chicken with Potato
 * Wedges & Milk"), because the plating-accurate label leads with the protein
 * even when the curated catalog title doesn't. The stored `menuItemName` is
 * the fallback ONLY when no named components exist (à-la-carte single items,
 * stale rows). Duplicate names collapse case-insensitively (first spelling
 * wins), mirroring the platform's title dedupe.
 */
export function serveDisplayName(
  menuItemName: string,
  components: ReadonlyArray<{ componentName: string; componentKind: string | null }> | null | undefined,
): string {
  const parts: ServeMealPart[] = []
  const seen = new Set<string>()
  for (const component of components ?? []) {
    const name = component.componentName.trim()
    const key = name.toLowerCase()
    if (name.length === 0 || seen.has(key)) {
      continue
    }
    seen.add(key)
    parts.push({ name, kind: component.componentKind ?? null })
  }
  if (parts.length === 0) {
    return menuItemName
  }
  const [head, ...rest] = orderMealPartsMainFirst(parts).map((entry) => entry.name)
  if (rest.length === 0) {
    return head!
  }
  const tail = rest.length === 1 ? rest[0]! : `${rest.slice(0, -1).join(', ')} & ${rest[rest.length - 1]}`
  return `${head} with ${tail}`
}

export interface StudentMealGroup {
  key: string
  studentUserId: string | null
  studentName: string
  allergens?: StudentAllergenWire[]
  /** `studentName` is written family name first — see PROD-1107. */
  familyNameFirst: boolean
  /** The student's scannable Ordo student ID (e.g. `LIN-X7K9P3`). */
  ordoStudentId: string | null
  /**
   * The school's own student number from the roster import
   * (`Membership.localStudentId`). This is the ID the school and its
   * cafeteria workers know the student by, so it is what the serve card
   * shows; `ordoStudentId` stays the scan/barcode identity.
   */
  localStudentId: string | null
  gradeRangeName: string | null
  rosterName: string | null
  /** The server-resolved serve bucket (Enrollment facts). */
  groupLabel: string | null
  /** Campus staff ordering for themselves (PROD-1439) — never a student. */
  isStaffDiner?: boolean
  items: ServiceOrderItemWire[]
  total: number
  served: number
  remaining: number
}

/**
 * Group order items by student for the students-known card grid. Totals count
 * SERVINGS (sum of row quantities), not rows, so a quantity-2 order line
 * reconciles with the servings-grained session counts everywhere else.
 */
export function groupStudentRows(items: ReadonlyArray<ServiceOrderItemWire>): StudentMealGroup[] {
  const map = new Map<string, StudentMealGroup>()
  for (const item of items) {
    if (item.studentUserId == null && item.studentName == null) {
      continue
    }
    const key = item.studentUserId ?? item.studentName ?? item.orderItemId
    const existing = map.get(key)
    if (!existing) {
      map.set(key, {
        key,
        studentUserId: item.studentUserId,
        studentName: item.studentName ?? item.studentUserId ?? 'Unassigned student',
        allergens: item.studentAllergens ?? [],
        familyNameFirst: item.studentFamilyNameFirst,
        ordoStudentId: item.ordoStudentId,
        localStudentId: item.localStudentId,
        gradeRangeName: item.gradeRangeName,
        rosterName: item.rosterName,
        groupLabel: item.groupLabel ?? null,
        isStaffDiner: item.isStaffDiner ?? false,
        items: [item],
        total: item.quantity,
        served: isServed(item) ? item.quantity : 0,
        remaining: isServed(item) ? 0 : item.quantity,
      })
      continue
    }
    existing.items.push(item)
    existing.total += item.quantity
    if (isServed(item)) {
      existing.served += item.quantity
    } else {
      existing.remaining += item.quantity
    }
  }
  return Array.from(map.values()).sort((a, b) => a.studentName.localeCompare(b.studentName))
}

export interface DeliverableLine {
  key: string
  /** The deliverable/package composition, e.g. "Cheeseburger + Seasoned Potato Wedges". */
  label: string
  portion: string | null
  /** Ordered serving count — a quantity-2 order line renders "2 Cheese Pizza Square". */
  quantity: number
  /**
   * The menu bucket (meal option) the ordered item came from — code for the
   * color (`menuBucketColorKey`), label for the chip text. Null for items
   * with no template-bucket provenance (à-la-carte extras, legacy rows).
   */
  bucket: DeliverableLineBucket | null
}

export interface DeliverableLineBucket {
  code: string | null
  label: string
}

/**
 * The template bucket an order item composes into, read off its display
 * groups (`source: 'TEMPLATE_BUCKET'`). One item belongs to at most one
 * bucket; `code` is null only for payloads cached before it shipped.
 */
export function orderItemBucket(item: ServiceOrderItemWire): DeliverableLineBucket | null {
  const group = item.menuProduct?.displayGroups.find((g) => g.source === 'TEMPLATE_BUCKET')
  if (!group) {
    return null
  }
  return { code: group.bucketCode ?? null, label: group.label }
}

/**
 * The DISTINCT buckets a student's order spans, in line order — what the
 * line list shows beside the name so a worker reads "Main" or "Veg" before
 * the student is even opened (PROD-1304). Distinct by code + label so two
 * items from the same bucket produce one chip.
 */
export function groupBuckets(group: Pick<StudentMealGroup, 'items'>): DeliverableLineBucket[] {
  const seen = new Set<string>()
  const buckets: DeliverableLineBucket[] = []
  for (const item of group.items) {
    const bucket = orderItemBucket(item)
    if (!bucket) {
      continue
    }
    const key = `${bucket.code ?? ''}::${bucket.label}`
    if (!seen.has(key)) {
      seen.add(key)
      buckets.push(bucket)
    }
  }
  return buckets
}

/**
 * One line per DELIVERABLE (package) in the student's meal — the HOT combo's
 * components join into "Halal Chicken + Seasoned Potato Wedges", the fruit and
 * milk are their own lines. Falls back to the flat component list when a meal
 * has no deliverable projection. Lines carry the ordered quantity so a
 * multi-serving order line isn't silently rendered as a single serving.
 * Shared by the on-screen serve cards and the printed serve roster so both
 * render a student's meal identically.
 *
 * Main-first everywhere (Tasker 6d441bd7): names within a package and the
 * lines themselves both order by `serveMealPartRank` — the entree package
 * leads, beverage-only lines trail, and everything else keeps authored order
 * (a line ranks by its BEST part, so a combo containing the entree is the
 * entree line).
 */
export function deliverableLines(group: StudentMealGroup): DeliverableLine[] {
  interface LineEntry {
    parts: ServeMealPart[]
    portion: string | null
    quantity: number
    bucket: DeliverableLineBucket | null
  }
  const byKey = new Map<string, LineEntry>()
  for (const item of group.items) {
    // Every line built from this item inherits its bucket; a line shared by
    // two items (same deliverable) keeps the first bucket it saw.
    const bucket = orderItemBucket(item)
    const units = item.menuProduct?.deliverableUnits ?? []
    if (units.length === 0) {
      for (const c of item.components) {
        const key = `comp:${c.componentId}`
        const entry = byKey.get(key) ?? {
          parts: [{ name: c.componentName, kind: c.componentKind ?? null }],
          portion: c.servingSize,
          quantity: 0,
          bucket,
        }
        entry.quantity += item.quantity
        byKey.set(key, entry)
      }
      if (item.components.length === 0) {
        const key = `mi:${item.menuItemId}`
        const entry = byKey.get(key) ?? {
          parts: [{ name: item.menuItemName, kind: null }],
          portion: item.servingSize,
          quantity: 0,
          bucket,
        }
        entry.quantity += item.quantity
        byKey.set(key, entry)
      }
      continue
    }
    // A deliverable line accrues the item's quantity ONCE per item — several
    // units under the same deliverable are one package, not extra servings.
    const keysThisItem = new Set<string>()
    for (const unit of units) {
      const key = unit.deliverableId ? `del:${unit.deliverableId}` : `mi:${item.menuItemId}:${unit.label}`
      const entry = byKey.get(key) ?? { parts: [], portion: unit.servingSize, quantity: 0, bucket }
      const name = unit.componentName ?? unit.label
      if (name && !entry.parts.some((part) => part.name === name)) {
        entry.parts.push({ name, kind: unit.componentKind })
      }
      if (!keysThisItem.has(key)) {
        keysThisItem.add(key)
        entry.quantity += item.quantity
      }
      byKey.set(key, entry)
    }
  }
  const lineRank = (parts: ReadonlyArray<ServeMealPart>): number =>
    parts.reduce((best, part) => Math.min(best, serveMealPartRank(part.kind, part.name)), Number.MAX_SAFE_INTEGER)
  return Array.from(byKey.entries())
    .map(([key, e], index) => ({ key, entry: e, index }))
    .sort((a, b) => lineRank(a.entry.parts) - lineRank(b.entry.parts) || a.index - b.index)
    .map(({ key, entry: e }) => ({
      key,
      label:
        orderMealPartsMainFirst(e.parts)
          .map((part) => part.name)
          .join(' + ') || 'Item',
      portion: e.portion,
      quantity: e.quantity,
      bucket: e.bucket,
    }))
}

export interface QuantityRow {
  menuItemId: string
  menuItemName: string
  imageUri: string | null
  servingSize: string | null
  gradeRangeId: string | null
  gradeRangeName: string | null
  /** Homeroom/group display name for group-bucketed items. */
  groupLabel: string | null
  total: number
  served: number
  remaining: number
}

/**
 * Group order items into per-(menuItem × grade-range × group) quantity rows.
 * Totals count SERVINGS (sum of row quantities), not rows.
 */
export function groupQuantityRows(items: ReadonlyArray<ServiceOrderItemWire>): QuantityRow[] {
  const map = new Map<string, QuantityRow>()
  for (const item of items) {
    const groupLabel = item.groupLabel ?? item.rosterName ?? null
    const key = `${item.menuItemId}::${item.gradeRangeId ?? ''}::${groupLabel ?? ''}`
    const existing = map.get(key)
    if (!existing) {
      map.set(key, {
        menuItemId: item.menuItemId,
        menuItemName: item.menuItemName,
        imageUri: item.imageUri,
        servingSize: item.servingSize,
        gradeRangeId: item.gradeRangeId,
        gradeRangeName: item.gradeRangeName,
        groupLabel,
        total: item.quantity,
        served: isServed(item) ? item.quantity : 0,
        remaining: isServed(item) ? 0 : item.quantity,
      })
      continue
    }
    existing.total += item.quantity
    if (isServed(item)) {
      existing.served += item.quantity
    } else {
      existing.remaining += item.quantity
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const grade = (a.gradeRangeName ?? '').localeCompare(b.gradeRangeName ?? '')
    return grade !== 0 ? grade : a.menuItemName.localeCompare(b.menuItemName)
  })
}

/** Local-state key for a school-serve deliverable row (deliverable × serve group). */
export function deliverableRowKey(row: ServeDeliverableRowWire): string {
  return `${row.deliverableKey}::${row.groupDimension ?? ''}::${row.groupValue ?? row.gradeGroupId ?? ''}`
}

/** One labeled half of a tally caption — renderers set the label semibold. */
export interface ServePortionCaptionPart {
  label: 'Serving size' | 'Utensils'
  value: string
}

/**
 * Caption under a tally row's package title, explicitly labeled:
 * "Serving size: 0.5 cup · Utensils: #16 Disher" with SEMIBOLD labels —
 * hence structured parts, not a flat string. Either half stands alone when
 * only one is authored; empty when neither is, so unauthored rows carry no
 * noise caption (PROD-984).
 */
export function servePortionCaptionParts(
  row: Pick<ServeDeliverableRowWire, 'portionLabel' | 'disherSize'>,
): ServePortionCaptionPart[] {
  const parts: ServePortionCaptionPart[] = []
  if (row.portionLabel) {
    parts.push({ label: 'Serving size', value: row.portionLabel })
  }
  if (row.disherSize) {
    parts.push({ label: 'Utensils', value: row.disherSize })
  }
  return parts
}

/** Group deliverable serve rows under their grade-group (or homeroom) heading. */
export function groupDeliverableRows(
  rows: ReadonlyArray<ServeDeliverableRowWire>,
): Array<{ label: string; rows: ServeDeliverableRowWire[] }> {
  const map = new Map<string, ServeDeliverableRowWire[]>()
  for (const row of rows) {
    // Group by the server-resolved serve-group label (line tuple);
    // age-group/homeroom display fields as fallback.
    const label = row.groupLabel ?? row.gradeGroupName ?? row.rosterName ?? 'All grades'
    const list = map.get(label) ?? []
    list.push(row)
    map.set(label, list)
  }
  return Array.from(map.entries())
    .map(([label, groupRows]) => ({ label, rows: groupRows }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
}

/**
 * "Total served" for school-serve: deliverable totals merged by package label
 * across grade groups, folding the local (uncommitted) tally over the
 * committed served counts. Shared by the serve page's summary rail and the
 * printed serve sheet so both report the same live numbers.
 */
export function mergeDeliverableTotals(
  rows: ReadonlyArray<ServeDeliverableRowWire>,
  quantityServed: Record<string, number>,
): ServiceComponentTotalWire[] {
  const byLabel = new Map<string, ServiceComponentTotalWire>()
  for (const row of rows) {
    const entry = byLabel.get(row.label) ?? {
      componentId: row.deliverableKey,
      componentName: row.label,
      componentKind: null,
      planned: 0,
      served: 0,
    }
    entry.planned += row.planned
    entry.served += quantityServed[deliverableRowKey(row)] ?? row.served
    byLabel.set(row.label, entry)
  }
  return Array.from(byLabel.values()).sort((a, b) => a.componentName.localeCompare(b.componentName))
}

/**
 * Live planned/remaining PACKAGE units for the school-serve table, folding the
 * local (uncommitted) tally over each row's committed served count. This is
 * the per-row sum the quantity table itself renders — the completion dialog,
 * summary rail, and mealtime rail must count in this same unit. (`items`
 * quantities count MEALS, and one meal fans out into several packages, so
 * subtracting package tallies from meal counts is never meaningful.) Each
 * row floors at 0 so an over-served row never masks another row's remainder.
 */
export function deliverableServeProgress(
  rows: ReadonlyArray<ServeDeliverableRowWire>,
  quantityServed: Record<string, number>,
): { planned: number; remaining: number } {
  let planned = 0
  let remaining = 0
  for (const row of rows) {
    planned += row.planned
    remaining += Math.max(0, row.planned - (quantityServed[deliverableRowKey(row)] ?? row.served))
  }
  return { planned, remaining }
}

// ---------------------------------------------------------------------------
// Production-record fill — collapse age-group edits into PR line × tier buckets
// ---------------------------------------------------------------------------

/** Local-state key for a production-fill sub-row (deliverable × serve group). */
export function productionFillRowKey(
  deliverableKey: string,
  row: Pick<ProductionFillGradeRow, 'gradeGroupId' | 'groupDimension' | 'groupValue'>,
): string {
  return `${deliverableKey}::${row.groupDimension ?? ''}::${row.groupValue ?? row.gradeGroupId ?? ''}`
}

/** Worker-editable quantities for one deliverable × grade-group row. */
export interface ProductionGradeEdit {
  /** School-entered total passed out — editable so retro days (no Serve-screen tallies) can be completed honestly. */
  served: number
  reimbursable: number
  nonReimbursable: number
  leftover: number
}

/**
 * One row of the edit map: the four interlocked counts plus, on the unified
 * production-record input (PROD-1279), the school's own Ordered / Prepared /
 * Received for the band. Three states per transport count: MISSING =
 * untouched (the input shows its prefill), `null` = the school cleared the box
 * (empty, omitted from the save), a number = what the box holds (saved onto the
 * record's bucket). Kept apart from `ProductionGradeEdit` so `keyof` of that
 * type stays the four count fields the shipped Service records task derives
 * over; that task never reads or writes the three optional ones.
 */
export interface ProductionRowEdit extends ProductionGradeEdit {
  ordered?: number | null
  prepared?: number | null
  received?: number | null
}

/**
 * The namespaces the unified input keeps its transport temperatures under in
 * the SAME `temps` map as the serving reading (`facility::<key>` = Kitchen °F,
 * `delivery::<key>` = Received °F), so "Save for later" carries them with no
 * second store and the shipped Service records task, which only ever looks up
 * bare deliverable keys, cannot see them. Transport-only rows (a truck line no
 * record deliverable claims) key their edits under `transport::<item>` for the
 * same reason. `overlayProductionEdits` carries all three prefixes even when the
 * fresh seed has no such key.
 */
export const FACILITY_TEMP_PREFIX = 'facility::'
export const DELIVERY_TEMP_PREFIX = 'delivery::'
export const TRANSPORT_ROW_PREFIX = 'transport::'

/** The `temps` key of an item's Kitchen °F on the unified input. */
export function facilityTempKey(itemKey: string): string {
  return `${FACILITY_TEMP_PREFIX}${itemKey}`
}

/** The `temps` key of an item's Received °F on the unified input. */
export function deliveryTempKey(itemKey: string): string {
  return `${DELIVERY_TEMP_PREFIX}${itemKey}`
}

/**
 * True for a `temps` key the unified input owns beside the bare serving keys:
 * an item's Kitchen °F / Received °F, and the SERVING reading of a
 * transport-only row, which lives under the bare `transport::<item>` key
 * (`RecordRow.tempStoreKey`) because such a row has no deliverable key of its
 * own. None of the three has a seed on a fresh base, so the overlay carries
 * them by prefix; without the third a drafted serving reading on such a row
 * was gone on reopen.
 */
export function isTransportTempKey(key: string): boolean {
  return (
    key.startsWith(FACILITY_TEMP_PREFIX) || key.startsWith(DELIVERY_TEMP_PREFIX) || key.startsWith(TRANSPORT_ROW_PREFIX)
  )
}

/** True for a `rows` key of a transport-only row (no record deliverable behind it). */
export function isTransportRowKey(key: string): boolean {
  return key.startsWith(TRANSPORT_ROW_PREFIX)
}

/**
 * One editable footer write-in row (re-served leftover, backup meal, dropped
 * meal) in the Complete-service wizard.
 */
export interface ProductionWriteInEdit {
  /** Server row id; null until the first save creates it. */
  id: string | null
  /** Stable local list key (new rows have no server id yet). */
  key: string
  kind: 'LEFTOVER' | 'BACKUP' | 'DROPPED'
  /**
   * Catalog identity of the item: LEFTOVER (the recent menu item the
   * carryover came from) and DROPPED (the day's meal that couldn't be
   * served, when the picked item carries one). Always null for BACKUP.
   */
  menuItemId: string | null
  menuItemName: string | null
  description: string
  quantity: number
  reimbursableQty: number
  nonReimbursableQty: number
  /**
   * True once the worker edited either side of the claim split directly —
   * until then reimbursable tracks quantity (the common case: every write-in
   * meal is a first serving) and non-reimbursable stays 0.
   */
  reimbursableTouched: boolean
}

/**
 * Local edit state for the Complete-service wizard, keyed by production-record
 * id → { per-(deliverable × grade) quantity rows, per-deliverable service temp,
 * footer write-ins }.
 */
export type ProductionEdits = Record<
  string,
  {
    rows: Record<string, ProductionRowEdit>
    temps: Record<string, number | null>
    writeIns: ProductionWriteInEdit[]
    /**
     * The task's "Time reading" (`HH:MM`, PROD-1279 D9), carried per record so
     * the existing draft persistence covers it. OPTIONAL: drafts persisted
     * before this shipped have no such key, and `overlayProductionEdits` only
     * carries keys the BASE already has — which is why `seedProductionEdits`
     * seeds it on every record.
     */
    readingTime?: string
    /**
     * PROD-1279 unified input — the Delivery card's "Time reading" (`HH:MM`)
     * and "Received by" for this record's trucks. Optional for the same
     * reason as `readingTime`; the shipped flows never write them.
     */
    deliveryReadingTime?: string
    receiverName?: string
  }
>

/**
 * Build the wizard's edit state from a freshly-fetched production-fill payload.
 * Quantities seed from each grade row; the service temp seeds from the stored
 * SERVICE reading (`value`) when present, else the chef's reading (`prefill`).
 * Pure so the page can re-seed whenever fresh server data arrives (it must not
 * latch onto a stale snapshot). Tested in `format.test.ts`.
 */
/**
 * Signature prefill for the Complete-service wizard: the signed-in
 * worker's display name (same first+last the rest of the app shows).
 * Empty string when the profile hasn't resolved — the input stays
 * editable either way.
 */
export function signatoryNameFor(user: { firstName: string; lastName: string } | null | undefined): string {
  if (!user) {
    return ''
  }
  return `${user.firstName} ${user.lastName}`.trim()
}

export function seedProductionEdits(fill: ProductionFillResponse): ProductionEdits {
  const edits: ProductionEdits = {}
  for (const mt of fill.mealtimes) {
    const rows: Record<string, ProductionRowEdit> = {}
    const temps: Record<string, number | null> = {}
    for (const deliverable of mt.deliverables) {
      for (const row of deliverable.gradeRows) {
        rows[productionFillRowKey(deliverable.deliverableKey, row)] = {
          served: row.served,
          reimbursable: row.reimbursable,
          nonReimbursable: row.nonReimbursable,
          leftover: row.leftover,
          // The school's STORED Ordered / Prepared / Received ride onto the
          // row only when they exist: a missing key means "untouched, show the
          // prefill", and a never-entered band must open that way rather than
          // as a deliberately cleared (null) box.
          ...(row.ordered != null ? { ordered: row.ordered } : {}),
          ...(row.prepared != null ? { prepared: row.prepared } : {}),
          ...(row.received != null ? { received: row.received } : {}),
        }
      }
      // Stored reading, else the cafeteria's own service-line reading (the
      // Temperature logs page — logged at the line, so it beats the chef's
      // upstream reading), else the chef's reading, else the class preset
      // (HOT 165 / CHILLED 38) — the same final fallback the Confirm-deliveries
      // wizard uses (`lineTempDefault`). Without it a classed deliverable with
      // no upstream reading seeds null and silently dead-ends the Sign &
      // complete button. Unclassed deliverables stay null: unknown class means
      // an honest manual reading.
      temps[deliverable.deliverableKey] =
        deliverable.serviceTemperature.value ??
        deliverable.serviceTemperature.cafeteria?.value ??
        deliverable.serviceTemperature.prefill ??
        (deliverable.temperatureClass ? (TEMP_CLASS_PREFILL_F[deliverable.temperatureClass] ?? null) : null)
    }
    edits[mt.productionRecordId] = {
      rows,
      temps,
      // Seeded blank so every record carries the field from the start: a
      // record the worker never opens still writes a `readingTime` when the
      // task's draft is saved. (Defensive — `overlayProductionEdits` carries a
      // draft's time whether or not the base already had the key.)
      readingTime: '',
      writeIns: (mt.writeIns ?? []).map((w) => ({
        id: w.id,
        key: w.id,
        kind: w.kind,
        menuItemId: w.menuItemId,
        menuItemName: w.menuItemName,
        description: w.description ?? '',
        quantity: w.quantity,
        reimbursableQty: w.reimbursableQty,
        nonReimbursableQty: w.nonReimbursableQty,
        // A stored row's split is an assertion — never re-sync it to quantity.
        reimbursableTouched: true,
      })),
    }
  }
  return edits
}

/**
 * True when a write-in row can't be saved yet: no quantity, or its identity
 * is missing (LEFTOVER needs the menu item, BACKUP/DROPPED need a
 * description). The wizard blocks Next on incomplete rows — the server
 * rejects them, and silently dropping a row the worker added would erase a
 * count on a compliance record.
 */
export function writeInRowIncomplete(writeIn: ProductionWriteInEdit): boolean {
  if (!Number.isInteger(writeIn.quantity) || writeIn.quantity <= 0) {
    return true
  }
  if (writeIn.kind === 'LEFTOVER') {
    return !writeIn.menuItemId
  }
  return writeIn.description.trim() === ''
}

/**
 * Collapse the wizard's write-in edits into the save payload. Served
 * write-ins (LEFTOVER/BACKUP) carry their entered claim split, clamped so
 * `reimbursable + nonReimbursable ≤ quantity` (the server hard-rejects
 * anything above). DROPPED rows were never served, so both counts pin to 0;
 * they keep their catalog identity (the day's meal that was lost), while
 * BACKUP rows — off-menu by definition — never carry one. Pure + unit-tested.
 */
export function writeInSaveEntries(writeIns: ProductionWriteInEdit[]): Array<{
  id: string | null
  kind: 'LEFTOVER' | 'BACKUP' | 'DROPPED'
  menuItemId: string | null
  description: string | null
  quantity: number
  reimbursableQty: number
  nonReimbursableQty: number
}> {
  return writeIns
    .filter((w) => !writeInRowIncomplete(w))
    .map((w) => {
      const reimbursable = w.kind === 'DROPPED' ? 0 : Math.max(0, Math.min(w.quantity, Math.trunc(w.reimbursableQty)))
      const nonReimbursable =
        w.kind === 'DROPPED' ? 0 : Math.max(0, Math.min(w.quantity - reimbursable, Math.trunc(w.nonReimbursableQty)))
      return {
        id: w.id,
        kind: w.kind,
        menuItemId: w.kind === 'BACKUP' ? null : w.menuItemId,
        description: w.description.trim() || null,
        quantity: w.quantity,
        reimbursableQty: reimbursable,
        nonReimbursableQty: nonReimbursable,
      }
    })
}

export interface ProductionFillSaveBucket {
  /** Portion tier (the PR bucket tier key). */
  gradeRangeId: string | null
  /** `(groupDimension, groupValue)` — the PR bucket identity (both or neither). */
  groupDimension: string | null
  groupValue: string | null
  servedQty: number
  reimbursableQty: number
  nonReimbursableQty: number
  leftoverQty: number
  /**
   * PROD-1279 unified input — present ONLY when every grade row of the
   * bucket's (tier, group) carried a value for that column; a blank sibling
   * means the school did not enter it, so the key is left out and the stored
   * column stays untouched. The shipped flows' rows never carry the fields,
   * so their payloads are byte-for-byte unchanged.
   */
  orderedQty?: number
  preparedQty?: number
  receivedQty?: number
}

export interface ProductionFillSaveLine {
  lineId: string | null
  bundleDeliverableId: string | null
  menuItemId: string | null
  buckets: ProductionFillSaveBucket[]
}

/**
 * Fan one service-line temperature reading out to the production-record temp
 * logs it should write — one per distinct line target the merged deliverable
 * spans, identified by its `bundleDeliverableId` (bundles, e.g. each bundle's
 * "Beverage") or `menuItemId` (à-la-carte components). Deduped by that identity.
 * Pure + tested.
 *
 * Targets with neither identity are skipped: a `{bundleDeliverableId:null,
 * menuItemId:null}` log can't be matched back on read (the draft loader keys
 * stored temps by one of those two ids), so persisting it would be a write-only
 * black hole. Returning [] for a target-less deliverable is correct — there is
 * nothing the reader could ever associate the reading with.
 */
export function serviceTempSaveEntries(
  deliverable: Pick<ProductionFillDeliverable, 'lineTargets'>,
  value: number,
): Array<{ bundleDeliverableId: string | null; menuItemId: string | null; value: number }> {
  const byHead = new Map<string, { bundleDeliverableId: string | null; menuItemId: string | null; value: number }>()
  for (const target of deliverable.lineTargets) {
    if (!target.bundleDeliverableId && !target.menuItemId) {
      continue
    }
    const head = target.bundleDeliverableId ? `del:${target.bundleDeliverableId}` : `mi:${target.menuItemId}`
    if (!byHead.has(head)) {
      byHead.set(head, {
        bundleDeliverableId: target.bundleDeliverableId,
        menuItemId: target.bundleDeliverableId ? null : target.menuItemId,
        value,
      })
    }
  }
  return Array.from(byHead.values())
}

/** Distribute `total` across `caps` greedily, never exceeding each slot's cap. */
function greedyDistribute(total: number, caps: number[]): number[] {
  const out = caps.map(() => 0)
  let remaining = Math.max(0, Math.round(total))
  for (let i = 0; i < caps.length && remaining > 0; i += 1) {
    const give = Math.max(0, Math.min(caps[i] ?? 0, remaining))
    out[i] = give
    remaining -= give
  }
  return out
}

/**
 * Like `greedyDistribute`, but the amount beyond the total capacity lands on
 * the last slot instead of being dropped. Over-service (walk-ins, seconds,
 * backup meals) is a legitimate count on a federal compliance record — the
 * wizard warns (`SERVED_EXCEEDS_PLANNED`) but must persist what the worker
 * entered, never silently truncate it to planned. Exported for the serve
 * page's completion commit, which distributes a row tally the same way.
 */
export function greedyDistributeWithOverflow(total: number, caps: number[]): number[] {
  const out = greedyDistribute(total, caps)
  if (out.length > 0) {
    const excess = Math.max(0, Math.round(total)) - out.reduce((sum, v) => sum + v, 0)
    if (excess > 0) {
      out[out.length - 1]! += excess
    }
  }
  return out
}

/**
 * Collapse one deliverable's age-group edits into the per-line × portion-tier
 * buckets the production record stores. Age groups that share a tier sum
 * together (the PR has no age-group axis); the tier total is then distributed
 * across the deliverable's constituent lines, capped by each line's planned —
 * except served, whose excess above planned overflows onto the last line so an
 * over-served total is preserved. Reimbursable + non-reimbursable are clamped
 * per bucket so their sum never exceeds served. Pure + unit-tested.
 */
export function distributeProductionFill(
  deliverable: Pick<ProductionFillDeliverable, 'gradeRows' | 'lineTargets'>,
): ProductionFillSaveLine[] {
  const headOf = (t: ProductionFillLineTarget): string =>
    t.bundleDeliverableId ? `del:${t.bundleDeliverableId}` : `mi:${t.menuItemId ?? ''}`

  // Sum sub-rows into (tier × group tuple) totals. The three transport counts
  // (PROD-1279 unified input) are ENTERED for the group only when every row in
  // it carries a value: a blank sibling means unknown, and a partial sum
  // stamped as the school's figure would under-count the band.
  const groups = new Map<
    string,
    {
      tier: string | null
      groupDimension: string | null
      groupValue: string | null
      served: number
      reimbursable: number
      nonReimbursable: number
      leftover: number
      ordered: number | null
      prepared: number | null
      received: number | null
    }
  >()
  const fold = (total: number | null, value: number | null | undefined, first: boolean): number | null => {
    if (value == null || (!first && total === null)) {
      return null
    }
    return (first ? 0 : total!) + value
  }
  for (const row of deliverable.gradeRows) {
    const key = `${row.tierGradeRangeId ?? ''}::${row.groupDimension ?? ''}::${row.groupValue ?? ''}`
    const existing = groups.get(key)
    const g = existing ?? {
      tier: row.tierGradeRangeId,
      groupDimension: row.groupDimension,
      groupValue: row.groupValue,
      served: 0,
      reimbursable: 0,
      nonReimbursable: 0,
      leftover: 0,
      ordered: null,
      prepared: null,
      received: null,
    }
    g.served += row.served
    g.reimbursable += row.reimbursable
    g.nonReimbursable += row.nonReimbursable
    g.leftover += row.leftover
    g.ordered = fold(g.ordered, row.ordered, existing === undefined)
    g.prepared = fold(g.prepared, row.prepared, existing === undefined)
    g.received = fold(g.received, row.received, existing === undefined)
    groups.set(key, g)
  }

  const linesByHead = new Map<string, ProductionFillSaveLine>()
  for (const g of groups.values()) {
    const targets = deliverable.lineTargets.filter((t) => (t.tierGradeRangeId ?? '') === (g.tier ?? ''))
    if (targets.length === 0) {
      continue
    }
    const plannedCaps = targets.map((t) => t.planned)
    const served = greedyDistributeWithOverflow(g.served, plannedCaps)
    const reimbursable = greedyDistribute(g.reimbursable, served)
    const nonReimbursable = greedyDistribute(
      g.nonReimbursable,
      served.map((s, i) => Math.max(0, s - (reimbursable[i] ?? 0))),
    )
    const leftover = greedyDistribute(g.leftover, plannedCaps)
    // Served's rule for the three transport counts too: an over-count is a
    // real figure on a compliance record, never truncated to the plan.
    const ordered = g.ordered === null ? null : greedyDistributeWithOverflow(g.ordered, plannedCaps)
    const prepared = g.prepared === null ? null : greedyDistributeWithOverflow(g.prepared, plannedCaps)
    const received = g.received === null ? null : greedyDistributeWithOverflow(g.received, plannedCaps)
    // A target that draws a ZERO share of a NON-zero entered total was not
    // entered by anyone: the school typed one number for the band and the greedy
    // fan simply ran out before this line. Writing `0` there would be marked
    // entered and beat the shipment fact on the document (a milk band sold
    // through two bundles printing "delivered 0" for the second). So the key is
    // left out and the server leaves that bucket untouched. Only a total the
    // school entered as 0 itself writes 0 on every drawing target.
    const enteredShare = (shares: number[] | null, total: number | null, i: number): Record<string, number> | null =>
      shares === null || total === null ? null : total === 0 || (shares[i] ?? 0) > 0 ? { value: shares[i] ?? 0 } : null
    targets.forEach((t, i) => {
      const head = headOf(t)
      let line = linesByHead.get(head)
      if (!line) {
        line = { lineId: t.lineId, bundleDeliverableId: t.bundleDeliverableId, menuItemId: t.menuItemId, buckets: [] }
        linesByHead.set(head, line)
      }
      line.buckets.push({
        gradeRangeId: g.tier,
        groupDimension: g.groupDimension,
        groupValue: g.groupValue,
        servedQty: served[i] ?? 0,
        reimbursableQty: reimbursable[i] ?? 0,
        nonReimbursableQty: nonReimbursable[i] ?? 0,
        leftoverQty: leftover[i] ?? 0,
        ...(enteredShare(ordered, g.ordered, i) === null ? {} : { orderedQty: ordered![i] ?? 0 }),
        ...(enteredShare(prepared, g.prepared, i) === null ? {} : { preparedQty: prepared![i] ?? 0 }),
        ...(enteredShare(received, g.received, i) === null ? {} : { receivedQty: received![i] ?? 0 }),
      })
    })
  }
  return Array.from(linesByHead.values())
}
